import React, { useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ListItem } from '../Dropdown';
import { getAuthUser } from '../../redux/selectors';
import { exportFiles, setSavedStatus } from '../../redux/actions';
import { getIsSavedSite, getIsLoggedIn } from '../../redux/selectors';
import { useMemo } from 'react';
import validateMembership from '../../util/validateMembership';
import { getIsLoadingExport } from '../../redux/selectors/site';

function NavItemMore({ exportJSON, importJSON }) {
    const params = useParams();
    const importRef = useRef();
    const history = useHistory();
    const dispatch = useDispatch();
    const user = useSelector(getAuthUser);
    const isSaved = useSelector(getIsSavedSite);
    const isLoggedIn = useSelector(getIsLoggedIn);
    const isLoadingExport = useSelector(getIsLoadingExport);

    const { siteId } = params;

    const isExportFilesAllowed = useMemo(() => {
        return validateMembership(['PLAN_2', 'PLAN_3'], user);
    }, [user]);

    const handleGoto = useCallback(
        (url) => {
            let ok = true;
            if (!isSaved) {
                ok = window.confirm(
                    'All unsaved changes will be lost! Click "Ok" to proceed.'
                );
            }
            if (ok) {
                history.push(url);
                // Site saved status update
                dispatch(setSavedStatus(true));
            }
        },
        [dispatch, history, isSaved]
    );

    const handleExportFiles = useCallback(() => {
        if (!isExportFilesAllowed || !siteId) return;
        dispatch(exportFiles(siteId));
    }, [dispatch, isExportFilesAllowed, siteId]);

    const handleClickImportJSON = React.useCallback(() => {
        importRef.current.click();
    }, [importRef]);

    const handleImportJSON = React.useCallback(
        (e) => {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.readAsText(file);
            reader.onload = () => {
                const data = JSON.parse(reader.result);
                if (data.pages) {
                    importJSON(data);
                }
            };
        },
        [importJSON]
    );

    return (
        <>
            {isLoggedIn && (
                <ListItem>
                    <span
                        onClick={handleExportFiles}
                        disabled={!isExportFilesAllowed || isLoadingExport}
                    >
                        <FontAwesomeIcon
                            fixedWidth
                            icon={['far', 'file-export']}
                        />{' '}
                        Export Files{' '}
                        {isLoadingExport && (
                            <FontAwesomeIcon spin icon={['far', 'spinner']} />
                        )}
                    </span>
                </ListItem>
            )}
            <ListItem>
                <span disabled>
                    <FontAwesomeIcon fixedWidth icon={['far', 'cog']} /> Builder
                    Preferences
                </span>
            </ListItem>
            <ListItem>
                <span onClick={handleClickImportJSON}>
                    <FontAwesomeIcon fixedWidth icon={['far', 'file-import']} />{' '}
                    Import JSON
                </span>
                <input
                    type="file"
                    ref={importRef}
                    style={{ display: 'none' }}
                    accept="application/json"
                    onChange={handleImportJSON}
                />
            </ListItem>

            <ListItem>
                <span onClick={exportJSON}>
                    <FontAwesomeIcon fixedWidth icon={['far', 'file-export']} />{' '}
                    Export as JSON
                </span>
            </ListItem>
            <ListItem>
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://help.dorik.io"
                >
                    <FontAwesomeIcon fixedWidth icon={['far', 'books']} />{' '}
                    Documentation
                </a>
            </ListItem>
            <ListItem>
                <span disabled>
                    <FontAwesomeIcon
                        fixedWidth
                        icon={['far', 'user-headset']}
                    />{' '}
                    Support
                </span>
            </ListItem>
            <ListItem>
                <span onClick={() => handleGoto('/')}>
                    <FontAwesomeIcon
                        fixedWidth
                        icon={['far', 'long-arrow-left']}
                    />{' '}
                    Back to Dashboard
                </span>
            </ListItem>
        </>
    );
}

export default React.memo(NavItemMore);
